/**
 * Connecting the finance system's ledger.
 *
 * The executive desks read revenue, operating cost and the plan, and the
 * platform holds none of it. Typing it into a form was honest and does not
 * survive a month-end. This reads the export the finance system already
 * produces — a trial balance, a management pack, a revenue report, as CSV or
 * a spreadsheet — and shows exactly what it proposes to do with every line
 * before anything is applied.
 *
 * Nothing is applied until the reader has seen three things: which account
 * became which line and under what rule, which accounts matched no rule and
 * are therefore counted nowhere, and whether the mapped totals agree with the
 * totals the file states about itself. A ledger import that silently loses a
 * line is worse than no ledger import, because the total still looks right.
 */
import { useState } from "react";
import { rowsToText } from "../../domain/testing";
import { financeOrigin, type Finance } from "../../domain/finance";
import { money } from "../../domain/intelligence";
import { readLedger, toFinance, type LedgerImport as Import, type Mapped } from "../../domain/financeFeed";
import { Button, Card } from "../../design/components";
import { t } from "../../i18n";
import s from "./Executive.module.css";

/** A ledger export as text the reader can take: CSV as it is, a workbook's first sheet row by row. */
export async function readLedgerFile(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx")) {
    const { default: readXlsxFile } = await import("read-excel-file/browser");
    const rows = await readXlsxFile(file);
    return rowsToText(rows as unknown as unknown[][]);
  }
  if (name.endsWith(".csv") || name.endsWith(".txt") || file.type.startsWith("text/")) return file.text();
  throw new Error(t("a ledger is read from a .csv or .xlsx export"));
}

const label = (m: Mapped): string => {
  const target = m.target;
  if (target.kind === "revenue") return t("Revenue · {label}", { label: t(target.label) });
  if (target.kind === "opex") return t("Operating cost · {label}", { label: t(target.label) });
  if (target.kind === "capex") return t("Capital");
  if (target.kind === "passengers") return t("Passengers");
  return target.of === "revenue" ? t("The file's own revenue total") : t("The file's own operating cost total");
};

export function LedgerImport({
  finance,
  who,
  onApply,
}: {
  finance: Finance;
  who: string;
  onApply: (f: Finance) => void;
}) {
  const [read, setRead] = useState<Import | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<string | null>(null);

  const take = async (file: File) => {
    setError(null);
    setApplied(null);
    setBusy(true);
    try {
      const text = await readLedgerFile(file);
      const r = readLedger(text);
      if (!r.rows.length) {
        setRead(null);
        setError(r.rejected[0]?.why ?? t("no rows could be read from that file"));
        return;
      }
      setRead(toFinance(r, finance, { file: file.name, by: who, at: new Date().toISOString() }));
    } catch (e) {
      setRead(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const cur = finance.currency;
  return (
    <Card
      title="The finance system's ledger"
      sub={financeOrigin(finance)}
      actions={
        finance.basis === "ledger" ? (
          <span className={s.tag} style={{ color: "var(--green)" }}>
            {t("CONNECTED")}
          </span>
        ) : (
          <span className={s.tag}>{t("NOT CONNECTED")}</span>
        )
      }
    >
      <p className="muted" style={{ fontSize: 13.5, margin: "0 0 10px", maxWidth: "80ch" }}>
        {t(
          "Drop the monthly export from the finance system. Every line is placed by a rule you can read, anything that matches no rule is listed rather than dropped, and the totals are checked against the file’s own before a single figure reaches a desk.",
        )}
      </p>

      <label className={s.file}>
        <input
          type="file"
          accept=".csv,.txt,.xlsx,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void take(f);
            e.target.value = "";
          }}
        />
        <span>{busy ? t("reading…") : t("Choose a ledger export — .csv or .xlsx")}</span>
      </label>

      {error && <p style={{ fontSize: 13.5, color: "var(--red)", marginTop: 8 }}>{error}</p>}

      {read && (
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <div>
            <div className="eyebrow">{t("What the file is")}</div>
            <div style={{ fontSize: 13.5, marginTop: 4 }}>
              {read.read.rejected.length
                ? t(
                    "{rows} row(s) read, {placed} placed, {unplaced} unplaced, {rejected} unreadable. Columns: {columns}.",
                    {
                      rows: read.read.rows.length,
                      placed: read.mapped.length,
                      unplaced: read.unmapped.length,
                      rejected: read.read.rejected.length,
                      columns: Object.entries(read.read.columns)
                        .map(([k, v]) => t("{column} from “{header}”", { column: k, header: String(v) }))
                        .join(", "),
                    },
                  )
                : t("{rows} row(s) read, {placed} placed, {unplaced} unplaced. Columns: {columns}.", {
                    rows: read.read.rows.length,
                    placed: read.mapped.length,
                    unplaced: read.unmapped.length,
                    columns: Object.entries(read.read.columns)
                      .map(([k, v]) => t("{column} from “{header}”", { column: k, header: String(v) }))
                      .join(", "),
                  })}
              {read.months.length
                ? ` ${t("Months {from} to {to}.", { from: read.months[0], to: read.months[read.months.length - 1] })}`
                : ` ${t("The file states no month.")}`}
            </div>
          </div>

          <div>
            <div className="eyebrow">{t("Does it add up?")}</div>
            <ul style={{ margin: "4px 0 0", paddingInlineStart: 18, fontSize: 13.5, display: "grid", gap: 3 }}>
              {read.reconciliation.lines.map((l) => (
                <li key={l} style={{ color: read.reconciliation.balanced ? "var(--muted)" : "var(--ink)" }}>
                  {l}
                </li>
              ))}
            </ul>
            <div style={{ fontSize: 13.5, marginTop: 6 }}>
              {t("Revenue {revenue} · operating cost {opex}", {
                revenue: money(Math.round(read.reconciliation.mappedRevenue), cur),
                opex: money(Math.round(read.reconciliation.mappedOpex), cur),
              })}
              {read.reconciliation.unmappedTotal
                ? ` · ${t("unplaced {amount}", { amount: money(Math.round(read.reconciliation.unmappedTotal), cur) })}`
                : ""}
            </div>
          </div>

          {read.unmapped.length > 0 && (
            <div>
              <div className="eyebrow">{t("Placed nowhere · {n}", { n: read.unmapped.length })}</div>
              <p className="muted" style={{ fontSize: 13, margin: "4px 0 6px" }}>
                {t(
                  "These are in the file and in none of the figures below. They are shown rather than swept into “other”, because a total that quietly absorbs what it could not classify is the failure this screen exists to prevent.",
                )}
              </p>
              <div className={s.scroll} style={{ maxHeight: 180 }}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>{t("Line")}</th>
                      <th>{t("Account")}</th>
                      <th>{t("Amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {read.unmapped.map((u) => (
                      <tr key={`${u.line}-${u.label}`}>
                        <td className={s.mono}>{u.line}</td>
                        <td>
                          {u.code ? `${u.code} · ` : ""}
                          {u.label}
                        </td>
                        <td className={s.num}>{money(Math.round(u.actual), cur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <div className="eyebrow">{t("Where each account went · {n}", { n: read.mapped.length })}</div>
            <div className={s.scroll} style={{ maxHeight: 260, marginTop: 4 }}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t("Account")}</th>
                    <th>{t("Became")}</th>
                    <th>{t("Because")}</th>
                    <th>{t("Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {read.mapped.map((m) => (
                    <tr key={`${m.line}-${m.label}`}>
                      <td>
                        {m.code ? `${m.code} · ` : ""}
                        {m.label}
                        {m.month ? ` · ${m.month}` : ""}
                      </td>
                      <td>{label(m)}</td>
                      <td className="muted">{t(m.why)}</td>
                      <td className={s.num}>{money(Math.round(m.actual), cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Button
              variant="primary"
              onClick={() => {
                onApply(read.finance);
                setApplied(
                  t("applied — every desk that reads money now reads {file}", {
                    file: String(read.finance.ledger?.file),
                  }),
                );
                setRead(null);
              }}
            >
              {t("Use these figures")}
            </Button>
            <Button onClick={() => setRead(null)}>{t("Discard")}</Button>
            <span className="muted" style={{ fontSize: 13 }}>
              {t("Applying replaces the figures on nine desks. Each will say where they came from.")}
            </span>
          </div>
        </div>
      )}

      {applied && <p style={{ fontSize: 13.5, color: "var(--green)", marginTop: 8 }}>{applied}</p>}

      {finance.basis === "ledger" && finance.ledger && !read && (
        <div style={{ fontSize: 13.5, marginTop: 10 }}>
          <div className="eyebrow">{t("Connected")}</div>
          <div style={{ marginTop: 4 }}>
            {finance.ledger.file} · {finance.ledger.period} ·{" "}
            {t("{rows} row(s), {placed} placed", { rows: finance.ledger.rows, placed: finance.ledger.mapped })}
            {finance.ledger.unmapped ? `, ${t("{n} unplaced", { n: finance.ledger.unmapped })}` : ""}
            {finance.ledger.rejected ? `, ${t("{n} unreadable", { n: finance.ledger.rejected })}` : ""}.{" "}
            {t("Imported by {who} on {date}.", {
              who: finance.ledger.importedBy,
              date: finance.ledger.importedAt.slice(0, 10),
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
